import { BookOpen, ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReading } from "@/contexts/ReadingContext";
import { useAuth } from "@/contexts/AuthContext";

function formatCount(n, locale) {
    return new Intl.NumberFormat(locale).format(n);
}

export default function ReadingStats({ compact = false }) {
    const { stats, ready } = useReading();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    if (!ready) return null;

    const isAuthed = !!(user && typeof user === "object");
    const topPct = stats.ranking?.top_percent;
    const community = stats.ranking?.community_size || 0;

    if (compact) {
        return (
            <div
                data-testid="reading-stats-compact"
                className="inline-flex items-center gap-2 text-xs text-[var(--ink-muted)]"
            >
                <BookOpen size={13} strokeWidth={1.5} className="text-[var(--accent)]" />
                <span>
                    <strong className="text-[var(--ink)] font-semibold">{stats.count}</strong>{" "}
                    {t("reading_stats.compact_read")}
                    {topPct != null && (
                        <>
                            <span className="mx-1.5 text-[var(--border-strong)]">·</span>
                            <span className="text-[var(--accent)] font-mono-num">Top {topPct}%</span>
                        </>
                    )}
                </span>
            </div>
        );
    }

    const countKey = stats.count === 1 ? "reading_stats.read_count_one" : "reading_stats.read_count_other";
    const showRank = topPct != null && stats.count > 0;

    return (
        <div
            data-testid="reading-stats"
            className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-6 py-5 flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between sm:text-left gap-4"
        >
            <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-[var(--accent)]/10 flex items-center justify-center">
                    <BookOpen size={20} strokeWidth={1.5} className="text-[var(--accent)]" />
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-0.5">
                        {t("reading_stats.eyebrow")}
                    </p>
                    <p
                        className="font-display text-2xl text-[var(--ink)]"
                        style={{ fontVariationSettings: '"wght" 500, "wdth" 95, "opsz" 32' }}
                        dangerouslySetInnerHTML={{
                            __html: t(countKey, { count: stats.count }),
                        }}
                    />
                </div>
            </div>

            <div className="flex flex-col items-center sm:items-end gap-1">
                {showRank ? (
                    <>
                        <p
                            className="font-display text-3xl text-[var(--accent)] font-mono-num"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 48' }}
                        >
                            Top {topPct}%
                        </p>
                        <p className="text-xs text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <Users size={11} strokeWidth={1.8} className="text-[var(--ink-dim)]" />
                            {t("reading_stats.community", { count: formatCount(community, i18n.language) })}
                        </p>
                    </>
                ) : community >= 50 ? (
                    // Only surface the community CTA when the count is
                    // genuinely impressive (≥50). Showing "Únete a 10 lectores"
                    // makes the site look empty rather than aspirational —
                    // we'd rather hide the line until the number sells itself.
                    <p className="text-xs text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.8} className="text-[var(--ink-dim)]" />
                        {t("reading_stats.join_community", { count: formatCount(community, i18n.language) })}
                    </p>
                ) : null}
                {!isAuthed && (
                    <Link
                        to="/acceder"
                        data-testid="reading-stats-login-cta"
                        className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] hover:text-[var(--ink)] transition-colors border border-[var(--accent)]/40 hover:border-[var(--ink)] rounded-full px-3 py-1.5"
                    >
                        <span>{t("reading_stats.login_cta")}</span>
                        <ArrowRight size={11} strokeWidth={1.8} />
                    </Link>
                )}
            </div>
        </div>
    );
}
